<template>
  <div class="page-container">
    <main class="main-content">
      <div class="page-inner-box detail-header">
        <el-breadcrumb separator-class="el-icon-arrow-right">
          <el-breadcrumb-item to="/">首页</el-breadcrumb-item>
          <el-breadcrumb-item to="/class">所有班级</el-breadcrumb-item>
          <el-breadcrumb-item>班级详情</el-breadcrumb-item>
        </el-breadcrumb>

        <!-- 班级简介卡片 -->
        <div class="hero-card">
          <div class="hero-body">
            <div class="thumb" :style="{ backgroundImage: `url(${classInfo.coverImage})` }"></div>
            <div class="info">
              <h1 class="title">{{ classInfo.className }}</h1>
              <div class="desc">{{ classInfo.subtitle }}</div>
              
              <div class="tags-row">
                <span v-if="classInfo.categoryName" class="tag-item blue-tag">分类：{{ classInfo.categoryName }}</span>
                <span class="tag-item green-tag">{{ classInfo.courseCount }}门课程</span>
                <span class="tag-item purple-tag">{{ classInfo.paperCount }}套试卷</span>
              </div>

              <div class="price-section">
                <div class="price-row">
                  <span class="currency">¥</span>
                  <span class="price">{{ (classInfo.price || 0).toFixed(2) }}</span>
                  <span v-if="classInfo.originalPrice" class="origin-price">原价：¥{{ (classInfo.originalPrice || 0).toFixed(2) }}</span>
                  <span class="student-count">{{ classInfo.salesCount || 0 }}人已购</span>
                </div>
                <div class="validity-row">
                  <span class="label">有效期：</span>
                  <span class="value">{{ classInfo.validityDays || 0 }}天</span>
                </div>
              </div>

              <div class="actions">
                <el-button class="buy-btn" type="primary" @click="handlePurchase">
                  {{ isPurchased ? '立即学习' : '立即购买' }}
                </el-button>
                <div class="collect-btn" @click="shareClass">
                  <i class="el-icon-share"></i>
                  <span>分享</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="page-inner-box detail-content">
        <div class="left-column">
          <!-- 班级目录 -->
          <div class="content-tabs">
            <el-tabs v-model="activeTab" class="custom-tabs">
              <el-tab-pane label="课程目录" name="courses">
                <div class="syllabus-header">
                  共{{ classInfo.courseCount || 0 }}门课程
                </div>
                <div class="syllabus-list">
                  <div v-if="classInfo.courses && classInfo.courses.length > 0">
                    <div 
                      v-for="(course, index) in classInfo.courses" 
                      :key="course.courseId" 
                      class="course-group"
                    >
                      <div class="course-title" @click="toggleCourse(index)">
                        <div class="course-title-left">
                          <span class="lesson-index">{{ index + 1 }}</span>
                          <i class="el-icon-video-play play-icon"></i>
                          <span class="course-name">{{ course.courseName || '课程' }}</span>
                          <span v-if="course.isRequired === 1" class="required-tag">必修</span>
                          <span v-else class="optional-tag">选修</span>
                        </div>
                        <div class="course-title-right">
                          <span v-if="course.progress != null" class="progress-text">学习进度：<span class="blue">{{ course.progress }}%</span></span>
                          <span v-else class="status-text">未开始</span>
                          <i :class="course.expanded ? 'el-icon-arrow-down' : 'el-icon-arrow-right'" class="arrow-icon"></i>
                        </div>
                      </div>
                      <div v-show="course.expanded" class="course-detail">
                        <div v-if="course.unitsLoading" class="course-loading">
                          <i class="el-icon-loading"></i> 加载中...
                        </div>
                        <div v-else-if="course.units && course.units.length > 0" class="course-units">
                          <div v-for="(unit, uIndex) in course.units" :key="uIndex" class="unit-group">
                            <div class="unit-title" @click.stop="toggleUnit(index, uIndex)">
                              <span>{{ unit.title }}</span>
                              <i :class="unit.expanded ? 'el-icon-arrow-down' : 'el-icon-arrow-right'"></i>
                            </div>
                            <div v-show="unit.expanded" class="lesson-list">
                              <div
                                v-for="(lesson, lIndex) in unit.lessons"
                                :key="lIndex"
                                class="lesson-item"
                                :class="{ 'is-free': lesson.isFree }"
                                @click.stop="handleLessonClick(course, unit, lesson)"
                              >
                                <div class="l-left">
                                  <i class="el-icon-video-play play-icon"></i>
                                  <span class="l-title">{{ lesson.title }}</span>
                                  <span v-if="lesson.isFree" class="free-tag">免费试看</span>
                                </div>
                                <div class="l-right">
                                  <span v-if="lesson.durationText" class="duration-text">{{ lesson.durationText }}</span>
                                  <i v-if="lesson.locked && !lesson.isFree" class="el-icon-lock lock-icon"></i>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div v-else class="course-empty">暂无章节</div>
                      </div>
                    </div>
                  </div>
                  <el-empty v-else description="暂无课程" />
                </div>
              </el-tab-pane>

              <el-tab-pane label="试卷目录" name="papers">
                <div class="syllabus-header">
                  共{{ classInfo.paperCount || 0 }}套试卷
                </div>
                <div class="syllabus-list">
                  <div v-if="classInfo.papers && classInfo.papers.length > 0">
                    <div 
                      v-for="(paper, index) in classInfo.papers" 
                      :key="paper.paperId" 
                      class="lesson-item"
                      :class="{ disabled: !canTakePaper(paper) }"
                      @click="goPaper(paper)"
                    >
                      <div class="l-left">
                        <span class="lesson-index">{{ index + 1 }}</span>
                        <i class="el-icon-edit-outline paper-icon"></i>
                        <span class="l-title">{{ paper.paperName || '试卷' }}</span>
                        <span class="paper-type-tag">{{ paper.paperType === 1 ? '练习试卷' : '模拟试卷' }}</span>
                      </div>
                      <div class="l-right">
                        <span v-if="!canTakePaper(paper)" class="disabled-text">{{ getPaperDisabledReason(paper) }}</span>
                        <span v-else class="status-text">可开始</span>
                        <i v-if="canTakePaper(paper)" class="el-icon-arrow-right arrow-icon"></i>
                        <i v-else class="el-icon-lock lock-icon"></i>
                      </div>
                    </div>
                  </div>
                  <el-empty v-else description="暂无试卷" />
                </div>
              </el-tab-pane>
            </el-tabs>
          </div>

          <!-- 结业考试信息 -->
          <div v-if="classInfo.finalExamId" class="section-card">
            <div class="section-header">
              <span class="section-title">结业考试</span>
            </div>
            <div class="section-body">
              <div class="exam-info">
                <div class="exam-item">
                  <span class="exam-label">考试名称：</span>
                  <span class="exam-value">{{ classInfo.finalExamName || '结业考试' }}</span>
                </div>
                <div class="exam-item">
                  <span class="exam-label">及格分数：</span>
                  <span class="exam-value">{{ classInfo.finalPassScore || 0 }}分</span>
                </div>
                <div class="exam-item">
                  <span class="exam-label">是否需要完成所有课程：</span>
                  <span class="exam-value">{{ classInfo.forceCompleteCourse === 1 ? '是' : '否' }}</span>
                </div>
                <div v-if="classInfo.enableCertificate === 1" class="exam-item">
                  <span class="exam-label">结业证书：</span>
                  <span class="exam-value">通过考试后可获得结业证书</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 班级介绍 -->
          <div class="section-card">
            <div class="section-header">
              <span class="section-title">班级介绍</span>
            </div>
            <div class="section-body">
              <div class="introduction" v-html="classInfo.introduction || '暂无介绍'"></div>
            </div>
          </div>
        </div>

        <div class="right-column">
          <!-- 学习进度卡片（已购买时显示） -->
          <div v-if="isPurchased" class="progress-card">
            <div class="progress-header">学习进度</div>
            <div class="progress-body">
              <div class="progress-item">
                <span class="progress-label">课程进度：</span>
                <el-progress :percentage="classProgress.courseProgress || 0" :stroke-width="10"></el-progress>
              </div>
              <div class="progress-item">
                <span class="progress-label">结业考试：</span>
                <span class="progress-value">
                  {{ getExamStatusText(classProgress.finalExamStatus) }}
                </span>
              </div>
              <div v-if="classProgress.finalExamScore != null" class="progress-item">
                <span class="progress-label">考试分数：</span>
                <span class="progress-value">{{ classProgress.finalExamScore }}分</span>
              </div>
              <div class="progress-item">
                <span class="progress-label">证书状态：</span>
                <span class="progress-value">
                  {{ classProgress.certificateStatus === 1 ? '已获得' : '未获得' }}
                </span>
              </div>
              <div v-if="classProgress.certificateNo" class="progress-item">
                <span class="progress-label">证书编号：</span>
                <span class="progress-value">{{ classProgress.certificateNo }}</span>
              </div>
              <el-button type="primary" size="small" @click="goStudy">进入学习</el-button>
            </div>
          </div>

          <!-- 班级信息卡片 -->
          <div class="info-card">
            <div class="info-header">班级信息</div>
            <div class="info-body">
              <div class="info-item">
                <span class="info-label">有效期：</span>
                <span class="info-value">{{ classInfo.validityDays || 0 }}天</span>
              </div>
              <div class="info-item">
                <span class="info-label">课程数量：</span>
                <span class="info-value">{{ classInfo.courseCount || 0 }}门</span>
              </div>
              <div class="info-item">
                <span class="info-label">试卷数量：</span>
                <span class="info-value">{{ classInfo.paperCount || 0 }}套</span>
              </div>
              <div class="info-item">
                <span class="info-label">购买人数：</span>
                <span class="info-value">{{ classInfo.salesCount || 0 }}人</span>
              </div>
              <div class="info-item">
                <span class="info-label">浏览次数：</span>
                <span class="info-value">{{ classInfo.viewCount || 0 }}次</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <QuickPurchaseDialog
      :visible.sync="showPurchaseDialog"
      bizType="class"
      :itemId="purchaseItemId"
      :title="purchaseTitle"
      :image="purchaseImage"
      :price="purchasePrice"
      @purchased="handlePurchased"
    />
  </div>
</template>

<script>
import { getClassDetail, getClassProgress } from '@/api/classApi'
import { getCourseUnitsLessons } from '@/api/courseApi'
import { createClassOrder } from '@/api/orderApi'
import { mapGetters } from 'vuex'
import QuickPurchaseDialog from '@/components/QuickPurchaseDialog.vue'

export default {
  name: 'ClassDetail',
  components: { QuickPurchaseDialog },
  computed: {
    ...mapGetters('auth', ['isLoggedIn'])
  },
  data() {
    return {
      loading: false,
      activeTab: 'courses',
      classInfo: {},
      classProgress: {},
      isPurchased: false,
      showPurchaseDialog: false,
      purchaseItemId: null,
      purchaseTitle: '',
      purchaseImage: '',
      purchasePrice: 0
    }
  },
  created() {
    this.fetchClassDetail()
  },
  methods: {
    async fetchClassDetail() {
      const id = this.$route.params.id
      if (!id) {
        this.$message.error('班级ID不存在')
        return
      }

      this.loading = true
      try {
        const res = await getClassDetail(id)
        if (res && res.code === 200 && res.data) {
          this.classInfo = res.data
          // 使用后端返回的isPurchased字段判断当前用户是否已购买
          this.isPurchased = !!res.data.isPurchased
          
          // 如果已购买，获取学习进度
          if (this.isPurchased) {
            this.fetchClassProgress(id)
          }
        } else {
          this.$message.error((res && res.message) || '获取班级详情失败')
        }
      } catch (err) {
        this.$message.error((err && err.message) || '获取班级详情失败')
      } finally {
        this.loading = false
      }
    },
    async fetchClassProgress(id) {
      try {
        const res = await getClassProgress(id)
        if (res && res.code === 200 && res.data) {
          this.classProgress = res.data
        }
      } catch (err) {
        console.error('获取学习进度失败', err)
      }
    },
    getExamStatusText(status) {
      const statusMap = {
        0: '未考试',
        1: '未通过',
        2: '已通过'
      }
      return statusMap[status] || '未考试'
    },
    handlePurchase() {
      if (this.isPurchased) {
        this.goStudy()
        return
      }

      const token = localStorage.getItem('token')
      if (!token) {
        this.$message.warning('请先登录')
        this.$router.push({ name: 'mobileLogin', query: { redirect: this.$route.fullPath } })
        return
      }

      this.$router.push({
        name: 'purchaseConfirm',
        params: { type: 'class', id: this.classInfo.id }
      })
    },
    handlePurchased() {
      this.isPurchased = true
      this.fetchClassProgress(this.classInfo.id)
    },
    async handleFreePurchase() {
      try {
        const res = await createClassOrder({
          classId: this.classInfo.id,
          payType: 1,
          cashAmount: 0,
          coinAmount: 0,
          couponAmount: 0
        })
        if (res && res.code === 200) {
          this.$message.success('购买成功')
          this.isPurchased = true
          this.fetchClassProgress(this.classInfo.id)
        } else {
          this.$message.error(res.message || '购买失败')
        }
      } catch (err) {
        this.$message.error(err.message || '购买失败')
      }
    },
    goStudy() {
      const classId = this.$route.params.id || this.classInfo.id
      this.$router.push({ name: 'classStudyDetail', params: { id: classId } })
    },
    async toggleCourse(index) {
      if (!this.classInfo.courses || index < 0 || index >= this.classInfo.courses.length) return
      const course = this.classInfo.courses[index]
      const willExpand = !course.expanded
      this.$set(this.classInfo.courses[index], 'expanded', willExpand)
      if (willExpand && !course.unitsLoaded) {
        this.$set(this.classInfo.courses[index], 'unitsLoading', true)
        try {
          const res = await getCourseUnitsLessons(course.courseId)
          const data = res && res.data
          const units = Array.isArray(data) ? data : []
          const sortByOrder = (items) => {
            const list = Array.isArray(items) ? items : []
            return list
              .map((item, idx) => {
                const orderRaw = item && (item.sortOrder ?? item.sort_order)
                const order = orderRaw == null ? null : Number(orderRaw)
                return { item, idx, order: Number.isNaN(order) ? null : order }
              })
              .sort((a, b) => {
                const ao = a.order
                const bo = b.order
                if (ao == null && bo == null) return a.idx - b.idx
                if (ao == null) return 1
                if (bo == null) return -1
                if (ao !== bo) return ao - bo
                return a.idx - b.idx
              })
              .map(row => row.item)
          }
          const sortedUnits = sortByOrder(units)
          const parsedUnits = sortedUnits.map((unit, uIndex) => {
            const lessons = (unit && unit.lessons) || []
            const sortedLessons = sortByOrder(Array.isArray(lessons) ? lessons : [])
            const unitId = unit && (unit.id ?? unit.unitId ?? unit.unit_id)
            const normalizedLessons = sortedLessons.map((lesson, lIndex) => {
              const allowTrial = Number(lesson && lesson.allowTrial) === 1
              const durationRaw = lesson && (lesson.duration ?? lesson.durationSeconds ?? lesson.duration_seconds)
              const duration = durationRaw == null ? null : Number(durationRaw)
              let durationText = ''
              if (duration && duration > 0) {
                const m = Math.floor(duration / 60)
                const s = duration % 60
                durationText = m > 0 ? `${m}分${s > 0 ? s + '秒' : ''}` : `${s}秒`
              }
              return {
                id: (lesson && (lesson.id ?? lesson.lessonId ?? lesson.lesson_id)) || `${unitId || uIndex}-${lIndex}`,
                title: (lesson && (lesson.lessonName ?? lesson.title ?? lesson.name)) || `课时${lIndex + 1}`,
                isFree: allowTrial,
                locked: !this.isPurchased && !allowTrial,
                durationText
              }
            })
            return {
              id: unitId,
              title: (unit && (unit.unitName ?? unit.title ?? unit.name)) || `单元${uIndex + 1}`,
              expanded: false,
              lessons: normalizedLessons
            }
          })
          this.$set(this.classInfo.courses[index], 'units', parsedUnits)
          this.$set(this.classInfo.courses[index], 'unitsLoaded', true)
        } catch (e) {
          this.$set(this.classInfo.courses[index], 'units', [])
          this.$set(this.classInfo.courses[index], 'unitsLoaded', true)
        } finally {
          this.$set(this.classInfo.courses[index], 'unitsLoading', false)
        }
      }
    },
    toggleUnit(courseIndex, unitIndex) {
      const course = this.classInfo.courses[courseIndex]
      if (!course || !course.units || unitIndex < 0 || unitIndex >= course.units.length) return
      this.$set(course.units[unitIndex], 'expanded', !course.units[unitIndex].expanded)
    },
    goCourseLearn(course) {
      if (!this.isPurchased) {
        this.$message.warning('请先购买班级')
        return
      }
      // 跳转到课程学习页面
      this.$router.push({ name: 'learn', params: { id: course.courseId } })
    },
    handleLessonClick(course, unit, lesson) {
      if (!lesson) return
      const courseId = course.courseId
      const lessonId = lesson.id
      const unitId = unit && unit.id

      // 免费试看课时：无需登录和购买即可学习
      if (lesson.isFree) {
        this.$router.push({
          name: 'learn',
          params: { id: courseId },
          query: { lessonId, unitId, trial: '1' }
        })
        return
      }

      // 非免费课时：需要登录
      if (!this.isLoggedIn) {
        this.$router.push({ name: 'login', query: { redirect: this.$route.fullPath } })
        return
      }

      // 非免费课时：需要购买班级
      if (!this.isPurchased) {
        this.$message.warning('请先购买班级后再学习')
        return
      }

      // 已购买：进入学习
      this.$router.push({
        name: 'learn',
        params: { id: courseId },
        query: { lessonId, unitId }
      })
    },
    canTakePaper(paper) {
      if (!this.isPurchased) {
        return false
      }
      // 如果设置了强制完成课程，检查课程进度
      if (this.classInfo.forceCompleteCourse === 1) {
        const progress = this.classProgress.courseProgress || 0
        if (progress < 100) {
          return false
        }
      }
      return true
    },
    getPaperDisabledReason(paper) {
      if (!this.isPurchased) {
        return '请先购买班级'
      }
      if (this.classInfo.forceCompleteCourse === 1) {
        const progress = this.classProgress.courseProgress || 0
        if (progress < 100) {
          return '需完成所有课程'
        }
      }
      return ''
    },
    goPaper(paper) {
      if (!this.canTakePaper(paper)) {
        this.$message.warning(this.getPaperDisabledReason(paper))
        return
      }
      // 跳转到试卷页面
      const isPracticePaper = Number(paper.paperType) === 3
      this.$router.push({
        path: isPracticePaper ? `/practice-detail/${paper.paperId}` : `/exam-detail/${paper.paperId}`
      })
    },
    shareClass() {
      // 分享功能
      this.$message.info('分享功能开发中')
    }
  }
}
</script>

<style scoped lang="scss">
.page-container { min-height: 100vh; display: flex; flex-direction: column; background-color: #f5f7fa; }
.main-content { flex: 1; padding-top: 20px; }

.detail-header {
  margin-bottom: 20px;
}

.hero-card {
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  margin-top: 16px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1);
}

.hero-body {
  display: flex;
  gap: 24px;
}

.thumb {
  width: 320px;
  height: 200px;
  border-radius: 8px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: #f5f7fa;
}

.info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.title {
  font-size: 24px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 12px 0;
}

.desc {
  font-size: 14px;
  color: #606266;
  margin-bottom: 16px;
}

.tags-row {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.tag-item {
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
}

.blue-tag {
  background: #ecf5ff;
  color: #409eff;
}

.green-tag {
  background: #f0f9ff;
  color: #67c23a;
}

.purple-tag {
  background: #fdf2ff;
  color: #9b59b6;
}

.price-section {
  margin-bottom: 20px;
}

.price-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 8px;
}

.currency {
  font-size: 16px;
  color: #f56c6c;
}

.price {
  font-size: 32px;
  font-weight: 600;
  color: #f56c6c;
}

.origin-price {
  font-size: 14px;
  color: #909399;
  text-decoration: line-through;
}

.student-count {
  font-size: 14px;
  color: #909399;
  margin-left: auto;
}

.validity-row {
  font-size: 14px;
  color: #606266;
}

.validity-row .label {
  color: #909399;
}

.actions {
  display: flex;
  gap: 16px;
  align-items: center;
}

.buy-btn {
  width: 160px;
  height: 44px;
  font-size: 16px;
}

.collect-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 16px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  cursor: pointer;
  color: #606266;
  font-size: 14px;
}

.collect-btn:hover {
  color: #409eff;
  border-color: #409eff;
}

.detail-content {
  display: flex;
  gap: 20px;
}

.left-column {
  flex: 1;
}

.right-column {
  width: 300px;
}

.section-card {
  background: #fff;
  border-radius: 8px;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #ebeef5;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.section-count {
  font-size: 14px;
  color: #909399;
}

.section-body {
  padding: 20px;
}

/* 内容标签页样式 */
.content-tabs {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1);
  margin-bottom: 20px;
}

.custom-tabs {
  padding: 0 20px;
}

.syllabus-header {
  padding: 16px 0;
  font-size: 14px;
  color: #909399;
  border-bottom: 1px solid #ebeef5;
}

.syllabus-list {
  padding: 0;
}

.course-group {
  border-bottom: 1px solid #f0f0f0;
}

.course-group:last-child {
  border-bottom: none;
}

.course-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
  cursor: pointer;
  transition: background 0.2s;
}

.course-title:hover {
  background: #f5f7fa;
}

.course-title-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.course-title-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.lesson-index {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #409eff;
  color: #fff;
  border-radius: 50%;
  font-size: 12px;
}

.play-icon {
  font-size: 20px;
  color: #409eff;
}

.course-name {
  font-size: 14px;
  color: #303133;
}

.required-tag {
  background: #ecf5ff;
  color: #409eff;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.optional-tag {
  background: #f0f9ff;
  color: #67c23a;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.progress-text {
  font-size: 12px;
  color: #909399;
}

.progress-text .blue {
  color: #409eff;
}

.status-text {
  font-size: 12px;
  color: #909399;
}

.arrow-icon {
  font-size: 14px;
  color: #909399;
  transition: transform 0.3s;
}

.course-detail {
  background: #fafafa;
  border-top: 1px solid #f0f0f0;
}

.course-loading,
.course-empty {
  padding: 20px;
  text-align: center;
  color: #909399;
  font-size: 13px;
}

.course-units {
  .unit-group {
    .unit-title {
      padding: 12px 20px;
      background: #eef2ff;
      color: #333;
      font-weight: 500;
      font-size: 13px;
      cursor: pointer;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 1px solid #fff;

      &:hover { background: #e0e7ff; }
    }

    .lesson-list {
      .lesson-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 20px 12px 36px;
        border-bottom: 1px solid #f0f0f0;
        font-size: 13px;
        cursor: pointer;

        &:hover { background: #f5f7fa; }
        &.is-free {
          background: #f0f9ff;
          &:hover { background: #e0f2fe; }
        }

        .l-left {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #333;

          .play-icon { color: #ccc; font-size: 14px; }
          .free-tag {
            background: #4034eb;
            color: #fff;
            font-size: 12px;
            padding: 1px 4px;
            border-radius: 2px;
            transform: scale(0.9);
          }
        }

        .l-right {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 12px;
          color: #999;

          .duration-text { color: #909399; }
          .lock-icon { color: #c0c4cc; font-size: 14px; }
        }
      }
    }
  }
}

.course-info {
  padding: 16px 20px;
}

.info-item {
  margin-bottom: 12px;
  font-size: 14px;
  color: #606266;
}

.info-label {
  color: #909399;
}

.info-value {
  color: #303133;
}

.info-actions {
  margin-top: 16px;
}

.paper-icon {
  font-size: 20px;
  color: #9b59b6;
}

.paper-type-tag {
  background: #fdf2ff;
  color: #9b59b6;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.disabled-text {
  font-size: 12px;
  color: #f56c6c;
}

.lock-icon {
  font-size: 14px;
  color: #c0c4cc;
}

.exam-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.exam-item {
  display: flex;
  font-size: 14px;
}

.exam-label {
  color: #909399;
  width: 160px;
}

.exam-value {
  color: #303133;
}

.introduction {
  font-size: 14px;
  color: #606266;
  line-height: 1.6;
}

.progress-card, .info-card {
  background: #fff;
  border-radius: 8px;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1);
}

.progress-header, .info-header {
  padding: 16px 20px;
  border-bottom: 1px solid #ebeef5;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.progress-body, .info-body {
  padding: 20px;
}

.progress-item {
  margin-bottom: 16px;
}

.progress-label {
  font-size: 14px;
  color: #909399;
  margin-bottom: 8px;
  display: block;
}

.progress-value {
  font-size: 14px;
  color: #303133;
}

.info-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  font-size: 14px;
}

.info-label {
  color: #909399;
}

.info-value {
  color: #303133;
}
</style>
